import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import i18next from "../../i18n";

import { useFileInputUploader } from "../../hooks";
import {
  useCreateOffer,
  useCreateMultipleOffer,
  useAddOffersToJointOrder,
  useDeclineByOrder,
  useDeclineOrderLine,
  useUndeclineOffer,
} from "../../hooks/useOffers";
import { useUser, useNotification } from "../../context";
import { buildOfferPayload } from "./buildOfferPayload";
import {
  makeOfferConditionsSchema,
  OfferConditionsForm,
} from "./offerConditionsSchema";
import {
  buildInitialOfferState,
  EMPTY_CONDITIONS,
} from "./initializeOfferState";
import { OrderResponse, OfferRequest } from "../../types";
import {
  linePriceInputsOf,
  linePriceTotals,
  pricedLinesOf,
} from "../../utils/linePricing";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JointStatus = any;

interface UseOfferSubmissionArgs {
  orders: OrderResponse[];
  isJointOrder: boolean;
  jointOrderOfferStatus: JointStatus;
  derivedMultipleOrderId: string;
  hasServiceMaterial: boolean;
  isOrderOpen: boolean;
  shouldUseMultipleRoute: boolean;
}

/**
 * Owns the offer-COMPOSITION concern of the seller submission screen: the
 * per-material offer state, the general-conditions RHF form, file upload, the
 * create/decline mutations, and the byte-equivalent payload assembly. Extracted
 * from `Offer.tsx` (Task 5) so the container stays a thin data-fetch + gating +
 * render shell, mirroring sub-flow B's `useOrderSubmission`. Page-level
 * redirects and chat stay in the container.
 */
export const useOfferSubmission = ({
  orders,
  isJointOrder,
  jointOrderOfferStatus,
  derivedMultipleOrderId,
  hasServiceMaterial,
  isOrderOpen,
  shouldUseMultipleRoute,
}: UseOfferSubmissionArgs) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { openNotification, openError } = useNotification();

  const [expandedMaterial, setExpandedMaterial] = useState<string | false>(
    false
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [materialOffers, setMaterialOffers] = useState<Record<string, any>>({});
  const [fileName, setFileName] = useState<string>("");
  const [openDeclineConfirm, setOpenDeclineConfirm] = useState(false);
  const [declinedLines, setDeclinedLines] = useState<Record<string, string>>(
    {}
  );

  const {
    data: uploadedFileUrl,
    mutate: uploadFile,
    isPending: isUploading,
  } = useFileInputUploader();

  const { mutate: createOffer, isPending: isCreatingOffer } = useCreateOffer(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (res: any) => {
      openNotification(i18next.t("tenders.seller.offerSent"));
      navigate(`/offer/${res.id}`);
    },
    (error) => openError(error)
  );

  const { mutate: createMultipleOffer, isPending: isCreatingMultipleOffer } =
    useCreateMultipleOffer(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (res: any) => {
        openNotification(i18next.t("tenders.seller.offerSent"));
        const resolvedMultipleOrderId =
          derivedMultipleOrderId || res[0]?.order?.multipleOrder?.id || "";
        navigate(
          `/offer/multiple/${res[0]?.order?.id}?multipleOrderId=${resolvedMultipleOrderId}`
        );
      },
      (error) => openError(error)
    );

  const { mutate: addOffersToJointOrder, isPending: isAddingToJointOrder } =
    useAddOffersToJointOrder(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (res: any) => {
        openNotification(i18next.t("tenders.seller.offerSent"));
        const resolvedMultipleOrderId =
          derivedMultipleOrderId || res[0]?.order?.multipleOrder?.id || "";
        navigate(
          `/offer/multiple/${res[0]?.order?.id}?multipleOrderId=${resolvedMultipleOrderId}`
        );
      },
      (error) => openError(error)
    );

  const { mutate: declineByOrder, isPending: isDeclining } = useDeclineByOrder(
    () => {
      openNotification(i18next.t("offer.declineOffer.declinedBanner"));
      navigate("/biddings");
    },
    (error) => openError(error)
  );

  // N10: per-item "No cotiza" for joint quotations — decline one line pre-send
  // and quote the rest. Maps each declined line's orderId -> its declined offerId.
  const { mutate: declineOrderLine } = useDeclineOrderLine(
    (offer) => {
      if (offer?.order?.id) {
        setDeclinedLines((prev) => ({ ...prev, [offer.order.id]: offer.id }));
      }
    },
    (error) => openError(error)
  );
  const { mutate: undeclineOffer } = useUndeclineOffer(
    (offer) => {
      if (offer?.order?.id) {
        setDeclinedLines((prev) => {
          const next = { ...prev };
          delete next[offer.order.id];
          return next;
        });
      }
    },
    (error) => openError(error)
  );

  const handleDeclineLine = (orderId: string) => declineOrderLine(orderId);
  const handleUndeclineLine = (orderId: string) => {
    const offerId = declinedLines[orderId];
    if (offerId) undeclineOffer(offerId);
  };

  const buyerPicksUp = orders.some((orderItem) => orderItem?.buyerPicksUp);

  const conditionsSchema = useMemo(
    () => makeOfferConditionsSchema(hasServiceMaterial, buyerPicksUp),
    [hasServiceMaterial, buyerPicksUp]
  );

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<OfferConditionsForm>({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resolver: yupResolver(conditionsSchema) as any,
      mode: "onSubmit",
      defaultValues: EMPTY_CONDITIONS,
    });

  // Pre-populate the per-material offers and the general conditions from the
  // order (and any existing joint offer), exactly as the pre-Carbon container
  // did. The actual derivation is a pure function (`buildInitialOfferState`,
  // Task 5 finding 2) so that business rule is unit-testable on its own;
  // this effect just applies the result to form/component state.
  useEffect(() => {
    if (orders.length === 0) return;

    const initialState = buildInitialOfferState(
      orders,
      isJointOrder,
      jointOrderOfferStatus
    );

    reset(initialState.formValues);
    setFileName(initialState.fileName);
    setMaterialOffers(initialState.materialOffers);
    setExpandedMaterial(initialState.expandedMaterial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, isJointOrder, jointOrderOfferStatus]);

  const hasAtLeastOneNewOffer = useMemo(
    () =>
      orders.some((orderItem) => {
        const offer = materialOffers[orderItem.id];
        if (
          !offer ||
          !offer.isComplete ||
          offer.isExisting ||
          !offer.price ||
          !offer.currency
        ) {
          return false;
        }
        if (orderItem.expireDate && !offer.expireDate) return false;
        return true;
      }),
    [orders, materialOffers]
  );

  const totalAmountsByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    orders.forEach((orderItem) => {
      const offer = materialOffers[orderItem.id];
      if (offer && offer.isComplete && offer.linePrices?.length) {
        // SCRUM-116: a line-priced service adds up per line, each line in its own currency.
        const lines = pricedLinesOf(orderItem);
        linePriceTotals(
          lines,
          linePriceInputsOf(lines, offer.linePrices, offer.currency)
        ).forEach(({ currency, total }) => {
          totals[currency] = (totals[currency] || 0) + total;
        });
      } else if (offer && offer.isComplete && offer.price && offer.currency) {
        const totalAmount = parseFloat(offer.price) * orderItem.quantity;
        totals[offer.currency] = (totals[offer.currency] || 0) + totalAmount;
      }
    });
    return totals;
  }, [orders, materialOffers]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMaterialOfferComplete = (orderId: string, offerData: any) => {
    setMaterialOffers((prev) => ({
      ...prev,
      [orderId]: { ...prev[orderId], ...offerData, isComplete: true },
    }));

    const currentIndex = orders.findIndex((o) => o.id === orderId);
    const nextIncompleteOrder = orders
      .slice(currentIndex + 1)
      .find((o) => !materialOffers[o.id]?.isComplete);
    setExpandedMaterial(nextIncompleteOrder ? nextIncompleteOrder.id : false);
  };

  const handleCancelOffer = (orderId: string) => {
    setMaterialOffers((prev) => ({
      ...prev,
      [orderId]: {
        price: "",
        currency: orders.find((o) => o.id === orderId)?.currency || "USD",
        comment: "",
        isComplete: false,
      },
    }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files;
    if (file && file[0]) {
      uploadFile(file[0]);
      setFileName(file[0].name);
    }
  };

  const onSubmitOffers = (values: OfferConditionsForm) => {
    if (!isOrderOpen || !user?.id) return;

    const generalConditions = {
      paymentMethod: values.paymentMethod,
      paymentTerm: values.paymentTerm,
      leadTime: values.leadTime,
      offerValidity: values.offerValidity,
      generalComments: values.generalComments,
      deliveryToBuyer: values.deliveryToBuyer,
      deliveryCostIncluded: values.deliveryCostIncluded,
      additionalDeliveryCost: values.additionalDeliveryCost,
      deliveryCostCurrency: values.deliveryCostCurrency,
      pickupAddress: values.pickupAddress,
    };

    const buildLineOffer = (orderItem: OrderResponse) =>
      buildOfferPayload({
        orderItem,
        offer: materialOffers[orderItem.id],
        generalConditions,
        user: { id: user.id! },
        hasServiceMaterial,
        uploadedFileUrl,
        buyerPicksUp,
      });

    if (shouldUseMultipleRoute) {
      const newOffers = orders
        .filter(
          (orderItem) =>
            materialOffers[orderItem.id]?.isComplete &&
            !materialOffers[orderItem.id]?.isExisting &&
            !declinedLines[orderItem.id]
        )
        .map((orderItem) => buildLineOffer(orderItem));

      if (
        jointOrderOfferStatus?.existingMultipleOfferId &&
        newOffers.length > 0
      ) {
        addOffersToJointOrder({
          multipleOrderId: derivedMultipleOrderId,
          offers: newOffers,
        });
      } else {
        createMultipleOffer(newOffers);
      }
    } else {
      createOffer(buildLineOffer(orders[0]) as OfferRequest);
    }
  };

  const onConfirmDecline = () => {
    if (orders[0]?.id) declineByOrder(orders[0].id);
    setOpenDeclineConfirm(false);
  };

  return {
    control,
    watch,
    setValue,
    submit: handleSubmit(onSubmitOffers),
    materialOffers,
    expandedMaterial,
    setExpandedMaterial,
    handleMaterialOfferComplete,
    handleCancelOffer,
    declinedLines,
    handleDeclineLine,
    handleUndeclineLine,
    fileName,
    isUploading,
    handleFileUpload,
    hasAtLeastOneNewOffer,
    totalAmountsByCurrency,
    isSubmitting:
      isCreatingOffer || isCreatingMultipleOffer || isAddingToJointOrder,
    openDeclineConfirm,
    setOpenDeclineConfirm,
    onConfirmDecline,
    isDeclining,
  };
};
